import { useEffect, useRef, useState } from "react";
import { api, API } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Package, AlertTriangle, Upload, Download, FileDown } from "lucide-react";

const rupee = (v) => "₹ " + Number(v||0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const emptyForm = { name: "", sku: "", unit: "pcs", price: 0, tax_pct: 18, stock: 0, low_stock_threshold: 5, notes: "", hsn: "", hsn_desc: "" };

function HsnPicker({ value, onPick }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!open) return;
    api.get("/hsn/suggestions", { params: q ? { q } : {} }).then(r => setItems(r.data));
  }, [q, open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Input value={q} onChange={e=>{ setQ(e.target.value); onPick({ hsn: e.target.value, hsn_desc: "" }); setOpen(true); }} placeholder="Type HSN or category…" data-testid="hsn-input"/>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[420px] max-h-[300px] overflow-y-auto p-1">
        {items.length === 0 && <p className="text-xs text-muted-foreground px-3 py-2">No matches — you can type any code.</p>}
        {items.map(s => (
          <button
            key={s.hsn}
            type="button"
            className="w-full text-left px-3 py-2 rounded-sm hover:bg-accent text-sm"
            onClick={()=>{ onPick({ hsn: s.hsn, hsn_desc: s.desc }); setQ(s.hsn); setOpen(false); }}
            data-testid={`hsn-opt-${s.hsn}`}
          >
            <div className="font-mono font-semibold text-primary">{s.hsn}</div>
            <div className="text-xs text-muted-foreground truncate">{s.desc}</div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [showLow, setShowLow] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [adjOpen, setAdjOpen] = useState(null);
  const [delta, setDelta] = useState(0);
  const fileRef = useRef(null);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/products", { params: showLow ? { low_only: true } : {} });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showLow]);

  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price), tax_pct: Number(form.tax_pct), stock: Number(form.stock), low_stock_threshold: Number(form.low_stock_threshold) };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); setForm(emptyForm); setEditing(null); load();
    } catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    try { await api.delete(`/products/${id}`); load(); } catch { toast.error("Admin only"); }
  };
  const adjust = async () => {
    try {
      await api.post(`/products/${adjOpen.id}/stock`, { delta: Number(delta), reason: "manual" });
      toast.success("Stock updated"); setAdjOpen(null); setDelta(0); load();
    } catch { toast.error("Failed"); }
  };

  const dl = (path, filename) => {
    const token = localStorage.getItem("gaze_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported: ${data.added} added, ${data.updated} updated, ${data.skipped} skipped`);
      load();
    } catch (err) { toast.error("Import failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const lowCount = items.filter(p => Number(p.stock) <= Number(p.low_stock_threshold)).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Stock</p>
          <h1 className="font-serif-elegant text-5xl mt-1">Inventory</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} data-testid="product-import-input"/>
          <Button variant="outline" onClick={()=>dl("/products/import/template", "products_template.csv")} data-testid="product-template-btn"><FileDown size={14} className="mr-2"/>Template</Button>
          <Button variant="outline" onClick={()=>fileRef.current?.click()} data-testid="product-import-btn"><Upload size={14} className="mr-2"/>Import CSV</Button>
          <Button variant="outline" onClick={()=>dl("/products/export/csv", "products.csv")} data-testid="product-export-btn"><Download size={14} className="mr-2"/>Export</Button>
          <Button variant={showLow ? "default" : "outline"} onClick={()=>setShowLow(v=>!v)} data-testid="low-stock-toggle" className={showLow ? "bg-primary hover:bg-[#984D38]" : ""}>
            <AlertTriangle size={14} className="mr-2"/>{showLow ? "Showing low stock" : `Low stock (${lowCount})`}
          </Button>
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(emptyForm); setEditing(null);}}}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-[#984D38]" data-testid="add-product-btn"><Plus size={14} className="mr-2"/>Add Product</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">{editing?"Edit":"New"} Product</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="product-name"/></div>
                <div><Label>SKU</Label><Input value={form.sku} onChange={e=>setForm({...form,sku:e.target.value})}/></div>
                <div><Label>Unit</Label><Input value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})}/></div>
                <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={e=>setForm({...form,price:e.target.value})} data-testid="product-price"/></div>
                <div><Label>Tax %</Label><Input type="number" value={form.tax_pct} onChange={e=>setForm({...form,tax_pct:e.target.value})}/></div>
                <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={e=>setForm({...form,stock:e.target.value})} data-testid="product-stock"/></div>
                <div><Label>Low stock alert at</Label><Input type="number" value={form.low_stock_threshold} onChange={e=>setForm({...form,low_stock_threshold:e.target.value})}/></div>
                <div className="col-span-2">
                  <Label>HSN code</Label>
                  <HsnPicker value={form.hsn} onPick={({hsn, hsn_desc})=>setForm({...form, hsn, hsn_desc: hsn_desc || form.hsn_desc})}/>
                  {form.hsn_desc && <p className="text-xs text-muted-foreground mt-1">{form.hsn_desc}</p>}
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-[#984D38]" data-testid="product-save">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {importResult && (importResult.errors?.length > 0 || importResult.skipped > 0) && (
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm">
              <p className="font-semibold text-amber-900">Import finished with warnings</p>
              <p className="text-amber-800">Added <b>{importResult.added}</b> · Updated <b>{importResult.updated}</b> · Skipped <b>{importResult.skipped}</b></p>
              {importResult.errors?.length > 0 && (
                <ul className="mt-2 text-xs text-amber-900 list-disc pl-4">
                  {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  {importResult.errors.length > 5 && <li>… and {importResult.errors.length - 5} more</li>}
                </ul>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={()=>setImportResult(null)}>Dismiss</Button>
          </div>
        </Card>
      )}

      <Card className="border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-secondary/50">
            <tr>
              <th className="py-3 px-4">Product</th>
              <th className="py-3 px-4">SKU</th>
              <th className="py-3 px-4">HSN</th>
              <th className="py-3 px-4 text-right">Price</th>
              <th className="py-3 px-4 text-right">Stock</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={7} className="py-12 text-center text-muted-foreground"><Package size={32} className="mx-auto mb-2 opacity-40"/>No products yet.</td></tr>}
            {items.map(p => {
              const low = Number(p.stock) <= Number(p.low_stock_threshold);
              return (
                <tr key={p.id} className="border-t hairline hover:bg-accent/30">
                  <td className="py-3 px-4">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.unit} · Tax {p.tax_pct}%</div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{p.sku || "-"}</td>
                  <td className="py-3 px-4">
                    {p.hsn ? <span className="font-mono text-xs bg-secondary px-2 py-1 rounded">{p.hsn}</span> : <span className="text-muted-foreground text-xs">-</span>}
                  </td>
                  <td className="py-3 px-4 text-right">{rupee(p.price)}</td>
                  <td className="py-3 px-4 text-right font-medium">{p.stock}</td>
                  <td className="py-3 px-4">
                    {low ? <Badge className="bg-red-100 text-red-800 border-red-200 border">Low</Badge>
                         : <Badge className="bg-green-100 text-green-800 border-green-200 border">OK</Badge>}
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={()=>setAdjOpen(p)} data-testid={`adjust-${p.id}`}>Adjust</Button>
                    <Button size="icon" variant="ghost" onClick={()=>{ setEditing(p); setForm({...emptyForm, ...p}); setOpen(true); }}><Pencil size={14}/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>del(p.id)}><Trash2 size={14} className="text-destructive"/></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!adjOpen} onOpenChange={(v)=>{ if(!v){ setAdjOpen(null); setDelta(0);} }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">Adjust Stock — {adjOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Current: <b>{adjOpen?.stock}</b>. Enter positive to add, negative to remove.</p>
            <div><Label>Delta</Label><Input type="number" value={delta} onChange={e=>setDelta(e.target.value)} data-testid="stock-delta"/></div>
          </div>
          <DialogFooter><Button onClick={adjust} className="bg-primary hover:bg-[#984D38]" data-testid="stock-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
